import React, { useEffect, useState } from 'react';
import { I18nextProvider } from 'react-i18next';
import { appI18n, getLanguageDir } from '../i18n/app';
import {
  setHelpLanguage,
  SUPPORTED_HELP_LANGUAGES,
  type HelpLanguage,
} from '../i18n/help';

interface AppI18nProviderProps {
  children: React.ReactNode;
}

/**
 * Provides the global application i18n instance to the React tree and keeps the
 * document `lang`/`dir` attributes in sync with the active language so RTL
 * languages (Arabic, Urdu) render correctly.
 */
const AppI18nProvider: React.FC<AppI18nProviderProps> = ({ children }) => {
  const [language, setLanguage] = useState(appI18n.language);

  useEffect(() => {
    const onLanguageChanged = (lng: string) => setLanguage(lng);
    appI18n.on('languageChanged', onLanguageChanged);
    return () => {
      appI18n.off('languageChanged', onLanguageChanged);
    };
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('lang', language);
    document.documentElement.setAttribute('dir', getLanguageDir(language));
    const base = language.split('-')[0] as HelpLanguage;
    setHelpLanguage(SUPPORTED_HELP_LANGUAGES.includes(base) ? base : 'en');
  }, [language]);

  return <I18nextProvider i18n={appI18n}>{children}</I18nextProvider>;
};

export default AppI18nProvider;
